import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  Observable,
  catchError,
  finalize,
  map,
  shareReplay,
  switchMap,
  throwError,
} from 'rxjs';
import { AuthService } from '../../auth/services/auth.service';
import { TokenStorageService } from '../../auth/services/token-storage.service';
import { logout } from '../../auth/store/auth.actions';

/**
 * The one renewal in flight, shared by every request that is waiting on it.
 *
 * Single-flight is kept by sharing an observable rather than by a boolean
 * latch and a callback queue. The old shape leaked: the latch was reset only
 * on settle, so a caller cancelled mid-renewal (a `switchMap` superseded, a
 * component destroyed) left it wedged for the life of the page (F-013), and a
 * refused renewal cleared the queue without telling anyone in it (F-010).
 * Here a cancelled subscriber does not cancel the renewal (`refCount: false`),
 * every subscriber sees the renewal's own error, and `finalize` clears the
 * slot whichever way it settles. Emits the new access token.
 */
let refresh$: Observable<string> | null = null;

function renewSession(
  authService: AuthService,
  tokenService: TokenStorageService,
  refreshToken: string,
  dispatchLogout: () => void
): Observable<string> {
  return authService.refreshToken(refreshToken).pipe(
    map(res => {
      const data = res?.data;
      const accessToken = data?.accessToken ?? null;
      const newRefreshToken = data?.refreshToken ?? null;
      if (!accessToken) {
        // A renewal that produced no token renewed nothing (F-011): treat it
        // as a refused renewal rather than replaying calls unauthenticated.
        throw new HttpErrorResponse({
          status: 401,
          statusText: 'Session renewal returned no token',
        });
      }
      tokenService.save(accessToken, newRefreshToken);
      return accessToken;
    }),
    catchError(error => {
      dispatchLogout();
      return throwError(() => error);
    }),
    finalize(() => {
      refresh$ = null;
    }),
    shareReplay({ bufferSize: 1, refCount: false })
  );
}

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  const tokenService = inject(TokenStorageService);
  const authService = inject(AuthService);
  const store = inject(Store);
  const dispatchLogout = () =>
    store.dispatch(logout({ source: 'interceptor' }));

  // Exclude refresh endpoint and OTP verify endpoint from getting Authorization header
  // OTP verify uses a different token (OTP verification token) from the verification response
  // Reset Google Auth endpoint (not initiate) uses verified OTP token from OTP verification
  const isRefreshEndpoint = req.url.includes('/api/auth/v1/refresh');
  const isOtpVerifyEndpoint = req.url.includes('/api/auth/v1/otp/verify');
  // Match exact reset endpoint, not the initiate endpoint
  const isResetGoogleAuthEndpoint =
    req.url.includes('/api/auth/v1/g-auth/reset') &&
    !req.url.includes('/api/auth/v1/g-auth/reset/initiate');
  const shouldSkipAuthHeader =
    isRefreshEndpoint || isOtpVerifyEndpoint || isResetGoogleAuthEndpoint;

  const accessToken = tokenService.getAccessToken();
  const authReq =
    accessToken && !shouldSkipAuthHeader
      ? req.clone({
          setHeaders: {
            Authorization: `Bearer ${accessToken}`,
          },
        })
      : req;

  const handle401 = (err: HttpErrorResponse): Observable<HttpEvent<any>> => {
    // The refresh call itself was refused. `renewSession` — its only caller —
    // signs the operator out; doing it here too dispatched `logout` twice, and
    // a synthetic error threw away the backend's reason (F-018).
    if (isRefreshEndpoint) {
      return throwError(() => err);
    }

    const refreshToken = tokenService.getRefreshToken();
    if (!refreshToken) {
      dispatchLogout();
      return throwError(() => new HttpErrorResponse({ status: 401 }));
    }

    refresh$ ??= renewSession(
      authService,
      tokenService,
      refreshToken,
      dispatchLogout
    );

    return refresh$.pipe(
      switchMap(newToken =>
        next(req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } }))
      )
    );
  };

  return next(authReq).pipe(
    catchError(err => {
      // A 401 on a call that carried no session credential is the endpoint
      // refusing the credential the operator is still proving — a wrong or
      // spent one-time code, an account refused after its second factor — not
      // a session that expired. There is nothing to renew and nobody to sign
      // out, and the server's own message must reach the operator intact
      // (F-272; the backend now tells a blocked member their standing only at
      // OTP verification, under decision D10.1).
      // The refresh call is the one bare request that is about a session, and
      // keeps its own handling.
      if (
        err instanceof HttpErrorResponse &&
        err.status === 401 &&
        authReq === req &&
        !isRefreshEndpoint
      ) {
        return throwError(() => err);
      }

      if (
        err instanceof HttpErrorResponse &&
        err.status === 401 &&
        req.url.includes('/api/')
      ) {
        // A wrong current password on /password/change is a 400
        // INVALID_CURRENT_PASSWORD from user-management, so every 401 here is
        // about the session. (The English-string carve-out that used to sit
        // here never matched a real response — F-012 — and threw on a
        // non-string message — F-015.)
        return handle401(err);
      }
      return throwError(() => err);
    })
  );
};
